import { type FC, useCallback, useContext } from "react";

import { Box, Flex, Text } from "@mantine/core";
import { Section } from "@snapchat/snap-design-system";
import { clsx } from "clsx";
import { useSetAtom } from "jotai";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import {
    CofConfigId,
    type Distribution,
    ExclusiveLensProgramStatus,
    type ExclusiveLensStatus,
    type GoogleProtobufTimestamp,
    LensCreatorPayoutEligibility,
    type Visibility,
} from "../../../gql-generated/types";
import { LensCreatorPayout } from "../../../lens-creator-payout/components";
import { ExclusiveLensToggle } from "../../../lens-creator-payout/components/ExclusiveLensToggle";
import { LensPayoutHistoryCard } from "../../../lens-creator-payout/components/LensPayoutHistoryCard";
import { RemovedExclusiveLensAlert } from "../../../lens-creator-payout/components/RemovedExclusiveLensAlert";
import {
    LensCreatorRewardsHeading,
    TextAcceptedLCRTosLink,
    TextAcceptedLensPlusTOS,
    TextAcceptedTos,
    TosModalHeader,
    ViewFullHistory,
} from "../../../lens-creator-payout/components/messages.generated";
import { LensGranularity } from "../../../lens-insights/models";
import { selectedLensAtom } from "../../../lens-insights/state";
import { TOS_KEY_MAP, TosModal, TosSource } from "../../../shared/components/tos";
import { useMonetizationState, useMonetizationTos, useSelectedOrganizationContainer } from "../../../shared/hooks";
import { type LensPreviewMediaItem } from "../../../shared/state";
import { LinkStyled, linkButtonElevatedClassName } from "../../../shared/styles";
import { timestampToDate } from "../../../shared/utils/date";
import { getExclusiveLensFlags } from "../../../shared/utils/monetizationUtils";
import {
    LensPlusPilotProgramOption,
    MonetizationDisabledMessage,
} from "../../../submit/components/shared/messages.generated";
import { useLensChangeTracker } from "../../hooks/useLensChangeTracker";
import { FieldLabel } from "../shared";

import { LensCreatorRewardsInsights } from "./LensCreatorRewardsInsights";
import styles from "./LensCreatorRewardsSection.module.css";
import { EnrolledProgramBody } from "./enrolled-program";
import {
    calculateSectionVisibility,
    getMonetizationHistoryPath,
    isCreatorPayoutDisabled,
    isCreatorPayoutEnrolled,
    isLensCreationWithinThreshold,
    resolveEnrolledProgram,
    shouldShowViewFullHistory,
} from "./lensCreatorRewardsUtils";

import { UserInfoContext } from "src/app/shared/api/contexts";
import { MonetizationTosWarning } from "src/app/submit/components/shared";

interface LensCreatorRewardsSectionProps {
    lensId: string;
    lensName: string;
    unlockableId: number;
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    distributionsList: readonly Distribution[];
    createdAt: GoogleProtobufTimestamp | undefined;
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null;
    previewMediaItems: LensPreviewMediaItem[];
    exclusiveLensStatus: ExclusiveLensStatus;
    visibility: Visibility;
    actionsDisabled: boolean;
    updateCreatorPayout: (enrolled: boolean) => void;
    creatorRewardProgramEnrolled: boolean | undefined;
}

export const LensCreatorRewardsSection: FC<LensCreatorRewardsSectionProps> = ({
    lensId,
    lensName,
    unlockableId,
    lensCreatorPayoutEligibility,
    distributionsList,
    createdAt,
    markedAsExclusiveAt,
    previewMediaItems,
    exclusiveLensStatus,
    visibility,
    actionsDisabled,
    updateCreatorPayout,
    creatorRewardProgramEnrolled,
}) => {
    const { configs } = useFeatureGates();
    const showLensCreatorRewardsDetails = configs[CofConfigId.MyLensesLensCreatorRewardsDetails].enabled;
    const isMyLensesCreatorPayoutEnabled = configs[CofConfigId.MyLensesCreatorPayout].enabled;
    const isMonetizationInsightsEnabled = configs[CofConfigId.MyLensesIlp].enabled;
    const isMonetizationHubEnabled = configs[CofConfigId.MyLensesMonetizationInsights].enabled;
    const userInfo = useContext(UserInfoContext);
    const { orgId, lensFolderId } = useSelectedOrganizationContainer();
    const setSelectedLens = useSetAtom(selectedLensAtom);
    const lensPlusEligible =
        userInfo?.data?.getUserProfile.exclusiveLensProgramStatus ===
        ExclusiveLensProgramStatus.ExclusiveLensProgramStatusEligible;

    const {
        loading: tosLoading,
        tos,
        acceptedLatestVersion,
        acceptedOldVersion,
        tosModalVisible,
        tosSource,
        triggerTosModal,
        closeTosModal,
        triggerTosAcceptance,
    } = useMonetizationTos();

    const { ownerMismatch, userLoading } = useMonetizationState({
        lensForUpdate: { distributionsList, lensCreatorPayoutEligibility },
    });

    const { hasFieldChanged } = useLensChangeTracker(lensId);

    const lensCreatorPayoutEnrolled =
        creatorRewardProgramEnrolled ?? isCreatorPayoutEnrolled(lensCreatorPayoutEligibility);

    const creatorPayoutEligible =
        lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible;

    const { hasExclusiveLensProgram, isExclusiveLens, isExclusiveLensPending } =
        getExclusiveLensFlags(exclusiveLensStatus);

    const handleExclusiveLensChange = useCallback(
        (isExclusive: boolean) => {
            // No phone gate needed: this page passes canToggleExclusiveLens={false}, so the
            // Lens+ toggle is read-only here and enrollment only happens in the submit flow.
            if (isExclusive && lensCreatorPayoutEnrolled) {
                updateCreatorPayout(false);
            }
        },
        [lensCreatorPayoutEnrolled, updateCreatorPayout],
    );

    const handleCreatorPayoutChange = useCallback(
        (enrolled: boolean) => {
            if (enrolled && hasExclusiveLensProgram) {
                // Note: exclusive lens changes not applicable for lens details page
            }
            updateCreatorPayout(enrolled);
        },
        [hasExclusiveLensProgram, updateCreatorPayout],
    );

    const handleTosAcceptance = useCallback(async () => {
        try {
            await triggerTosAcceptance();

            if (tosSource === TosSource.CreatorPayout && !actionsDisabled && !lensCreatorPayoutEnrolled) {
                updateCreatorPayout(true);
            }
        } catch (error) {
            console.error("Failed to accept TOS:", error);
        }
    }, [triggerTosAcceptance, tosSource, actionsDisabled, lensCreatorPayoutEnrolled, updateCreatorPayout]);

    const creationDate = timestampToDate(createdAt);
    const lensCreationWithinThreshold = isLensCreationWithinThreshold(creationDate);

    const dataLoading = tosLoading || userLoading;

    const {
        shouldShowExclusiveLensToggle,
        shouldShowRemovedExclusiveLensWarning,
        shouldShowTopPerformerPayoutsSection,
        shouldShowInsights,
    } = calculateSectionVisibility(
        isMyLensesCreatorPayoutEnabled,
        showLensCreatorRewardsDetails,
        lensPlusEligible,
        exclusiveLensStatus,
        lensCreatorPayoutEligibility,
    );

    const shouldShowLegacyLensPlus =
        shouldShowExclusiveLensToggle && (!isMonetizationHubEnabled || hasExclusiveLensProgram);
    const shouldShowLegacyTopPerformer =
        shouldShowTopPerformerPayoutsSection && (!isMonetizationHubEnabled || !hasExclusiveLensProgram);

    const exclusiveLensSectionClasses = clsx(
        !lensPlusEligible && styles.noPaddingDiv,
        lensPlusEligible && isExclusiveLens && styles.sectionEnrolled,
        lensPlusEligible && isExclusiveLensPending && styles.sectionPending,
    );

    const creatorPayoutSectionClass = clsx(lensCreatorPayoutEnrolled && styles.sectionEnrolled);

    const enrolledProgram = isMonetizationHubEnabled
        ? resolveEnrolledProgram({
              isExclusiveLens,
              // Saved enrollment, not the draft toggle: the panel swaps in only once the change is saved.
              topPerformerEnrolled: isCreatorPayoutEnrolled(lensCreatorPayoutEligibility),
              hasLensPlusHistory: shouldShowRemovedExclusiveLensWarning || Boolean(markedAsExclusiveAt),
          })
        : null;
    // The panel is gated on the latest TOS too; until then the header link is the only way to the hub.
    const showsEnrolledPanel = Boolean(enrolledProgram) && acceptedLatestVersion;

    // A panel is always about this lens, including one still in its Top Performer window.
    const hasLensPayout = creatorPayoutEligible || isExclusiveLens || showsEnrolledPanel;
    const showViewFullHistory = shouldShowViewFullHistory({
        creatorPayoutEligible,
        isExclusiveLens,
        isMonetizationInsightsEnabled,
        isRemovedFromLensPlus: shouldShowRemovedExclusiveLensWarning,
    });

    const monetizationBasePath = getMonetizationHistoryPath({
        isHubEnabled: isMonetizationHubEnabled,
        isExclusiveLens,
        orgId,
        lensFolderId,
    });

    const monetizationSearchParams = new URLSearchParams(
        hasLensPayout ? { lensId, timeRange: LensGranularity.Lifetime } : { timeRange: LensGranularity.Lifetime },
    );
    if (isMonetizationHubEnabled) {
        monetizationSearchParams.set("view", "all-time");
    }
    const monetizationHubUrl = `${monetizationBasePath}?${monetizationSearchParams.toString()}`;

    const handleViewFullHistory = useCallback(() => {
        if (hasLensPayout) {
            setSelectedLens({ id: String(unlockableId), name: lensName });
        }
    }, [hasLensPayout, setSelectedLens, unlockableId, lensName]);

    if (!isMyLensesCreatorPayoutEnabled) {
        return null;
    }

    return (
        <Flex direction="column">
            <Flex align="center" justify="space-between">
                <FieldLabel hasChanges={hasFieldChanged("creatorRewardProgramEnrolled")}>
                    <h3 className="heading-h500">
                        <LensCreatorRewardsHeading />
                    </h3>
                </FieldLabel>
                {showViewFullHistory && !tosLoading && !showsEnrolledPanel && (
                    <LinkStyled
                        className={linkButtonElevatedClassName}
                        to={monetizationHubUrl}
                        onClick={handleViewFullHistory}
                    >
                        <ViewFullHistory />
                    </LinkStyled>
                )}
            </Flex>
            {shouldShowRemovedExclusiveLensWarning && (
                <Section className={exclusiveLensSectionClasses}>
                    <RemovedExclusiveLensAlert />
                </Section>
            )}
            {acceptedLatestVersion ? (
                <>
                    <Flex direction="column" mt="md">
                        {enrolledProgram && (
                            <EnrolledProgramBody
                                program={enrolledProgram}
                                lensId={lensId}
                                unlockableId={String(unlockableId)}
                                lensName={lensName}
                                markedAsExclusiveAt={markedAsExclusiveAt}
                                monetizationUrl={monetizationHubUrl}
                                onViewInMonetization={handleViewFullHistory}
                                topPerformerInsights={
                                    shouldShowInsights &&
                                    creationDate && (
                                        <LensCreatorRewardsInsights creationDate={creationDate} lensId={lensId} />
                                    )
                                }
                            />
                        )}
                        {/* Lens+ Payouts Section */}
                        {!enrolledProgram && shouldShowLegacyLensPlus && (
                            <Section className={exclusiveLensSectionClasses}>
                                <Flex direction="column" gap="md">
                                    <ExclusiveLensToggle
                                        previewMediaItems={previewMediaItems}
                                        lens={undefined}
                                        exclusiveLensStatus={exclusiveLensStatus}
                                        onExclusiveLensChange={(isExclusive: boolean) => {
                                            handleExclusiveLensChange(isExclusive);
                                        }}
                                        disabled={actionsDisabled || lensCreatorPayoutEnrolled}
                                        acceptedLatestVersion={acceptedLatestVersion}
                                        acceptedOldVersion={acceptedOldVersion}
                                        tosLoading={dataLoading}
                                        triggerTosModal={() => {
                                            triggerTosModal(TosSource.ExclusiveLens);
                                        }}
                                        canToggleExclusiveLens={false}
                                        isExclusiveChecked={undefined}
                                        visibility={visibility}
                                        overrideContainerCss={lensPlusEligible ? styles.noPadding : ""}
                                    />
                                    {isExclusiveLens && (
                                        <LensPayoutHistoryCard
                                            unlockableId={String(unlockableId)}
                                            showSubscriberActions
                                            markedAsExclusiveAt={markedAsExclusiveAt}
                                        />
                                    )}
                                </Flex>
                            </Section>
                        )}
                        {/* Top Performer Payouts Section */}
                        {!enrolledProgram && shouldShowLegacyTopPerformer && (
                            <Section className={creatorPayoutSectionClass}>
                                <Flex direction="column" gap="md">
                                    <Box>
                                        <LensCreatorPayout
                                            lensCreatorPayoutEnrolled={lensCreatorPayoutEnrolled}
                                            setLensCreatorPayoutEnrolled={(enrolled: boolean) => {
                                                handleCreatorPayoutChange(enrolled);
                                            }}
                                            lensCreatorPayoutEligibility={lensCreatorPayoutEligibility}
                                            ownerMismatch={ownerMismatch}
                                            blockUnenrollment={true}
                                            disabled={isCreatorPayoutDisabled(
                                                actionsDisabled,
                                                visibility,
                                                hasExclusiveLensProgram,
                                                lensCreatorPayoutEligibility,
                                            )}
                                            lensCreationWithinThreshold={lensCreationWithinThreshold}
                                            loading={dataLoading}
                                            triggerTosModal={() => {
                                                triggerTosModal(TosSource.CreatorPayout);
                                            }}
                                            overrideContainerCss={styles.noPadding}
                                        />
                                        {hasExclusiveLensProgram && (
                                            <Text size="xs" c="dimmed" mt="xs">
                                                <MonetizationDisabledMessage
                                                    conflictingOption={<LensPlusPilotProgramOption />}
                                                />
                                            </Text>
                                        )}
                                    </Box>

                                    {/* Integrated Insights */}
                                    {shouldShowInsights && creationDate && (
                                        <LensCreatorRewardsInsights creationDate={creationDate} lensId={lensId} />
                                    )}

                                    {creatorPayoutEligible && (
                                        <LensPayoutHistoryCard unlockableId={String(unlockableId)} />
                                    )}
                                </Flex>
                            </Section>
                        )}
                    </Flex>
                    <p className={"paragraph-p300"}>
                        <TextAcceptedTos
                            lensPlusPayoutsTermsLink={
                                <button
                                    type="button"
                                    className={styles.tosLink}
                                    disabled={dataLoading}
                                    onClick={() => {
                                        triggerTosModal(TosSource.ExclusiveLens);
                                    }}
                                >
                                    <TextAcceptedLensPlusTOS />
                                </button>
                            }
                            topPerformerPayoutsTermsLink={
                                <button
                                    type="button"
                                    className={styles.tosLink}
                                    disabled={dataLoading}
                                    onClick={() => {
                                        triggerTosModal(TosSource.CreatorPayout);
                                    }}
                                >
                                    <TextAcceptedLCRTosLink />
                                </button>
                            }
                        />
                    </p>
                </>
            ) : (
                <MonetizationTosWarning
                    dataLoading={dataLoading}
                    triggerTosModal={() => {
                        triggerTosModal(TosSource.CreatorPayout);
                    }}
                    showFooterBg={false}
                />
            )}

            {/* Shared TOS Modal */}
            <TosModal
                tosHeading={<TosModalHeader />}
                tosMetadata={tos?.metadata ?? undefined}
                visible={tosModalVisible}
                disabled={dataLoading}
                loading={dataLoading}
                accepted={acceptedLatestVersion}
                onAccept={handleTosAcceptance}
                onDismiss={closeTosModal}
                initialStepKey={tosSource ? TOS_KEY_MAP[tosSource] : undefined}
            />
        </Flex>
    );
};
